import { useEffect, useRef, useState, type ElementType, type ReactNode } from "react";
import { cn } from "@/lib/utils";


type Direction = "up" | "down" | "left" | "right" | "none";

function isInViewport(el: Element) {
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const vw = window.innerWidth || document.documentElement.clientWidth;
  return r.bottom >= 0 && r.right >= 0 && r.top <= vh && r.left <= vw;
}

export function Reveal({
  children,
  className,
  delay = 0,
  direction = "up",
  as: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  direction?: Direction;
  as?: ElementType;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      setVisible(true);
      return;
    }

    // If already in viewport on mount (e.g. back-nav with restored scroll), show immediately.
    if (isInViewport(el)) {
      setVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px 25% 0px" },
    );
    io.observe(el);

    // Safety net: if the observer never fires (bfcache, layout race), reveal after 600ms.
    const fallback = window.setTimeout(() => setVisible(true), 600);

    // bfcache: pages restored from back-forward cache don't re-mount but should be visible.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setVisible(true);
    };
    window.addEventListener("pageshow", onPageShow);

    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  const offset: Record<Direction, string> = {
    up: "translate3d(0, 16px, 0)",
    down: "translate3d(0, -16px, 0)",
    left: "translate3d(16px, 0, 0)",
    right: "translate3d(-16px, 0, 0)",
    none: "none",
  };

  const Component = Tag as ElementType;

  return (
    <Component
      ref={ref as React.Ref<HTMLElement>}
      className={cn("will-change-transform", className)}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : offset[direction],
        transition:
          "opacity 400ms cubic-bezier(0.22, 1, 0.36, 1), transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </Component>
  );
}
